// UI-3: Popover, Menu (and the ⋯ OverflowMenu), RichDropdown and MoveToSheet.
// design-brief.md "Popovers / dropdowns"; mobile-responsive-addendum.md 6 (on a phone they are
// bottom sheets) and 7 (Move to... is the tap alternative to every drag).
//
// On tablet and desktop a popover is drawn in a portal at a fixed position worked out from its
// trigger, so a card's overflow: hidden cannot clip it (UI-2's ⋯ menu inside a Card footer was
// clipped that way), it flips above the trigger when there is no room below, and it stays inside
// the screen. On a phone every one of them is the same Sheet (Overlays.jsx).
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { v } from './tokens';
import { useBreakpoint } from './breakpoints';
import { Sheet } from './Overlays';
import { Button } from './Button';
import { Input } from './Inputs';
import './v2.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// Where a desktop popover goes: below the trigger (above if it does not fit), right or left
// aligned, clamped 8px inside the window. Re-measured on scroll and resize.
function usePosition(anchorRef, popRef, open, align) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      const p = popRef.current;
      if (!a || !p) return;
      const w = p.offsetWidth; const h = p.offsetHeight;
      const below = a.bottom + 6;
      const top = below + h > window.innerHeight - 8 && a.top - h - 6 > 8 ? a.top - h - 6 : below;
      const left = Math.min(Math.max(8, align === 'left' ? a.left : a.right - w), window.innerWidth - w - 8);
      setPos({ top: Math.round(top), left: Math.round(left) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, align]); // eslint-disable-line react-hooks/exhaustive-deps
  return pos;
}

// The bare popover. On a phone: a Sheet. Elsewhere: a portal next to the trigger; a press
// outside closes it (without moving focus); Escape closes it and focus goes back to the trigger.
export function Popover({ anchorRef, open, onClose, title, align = 'right', className, children, onKeyDown, id, role, label }) {
  const phone = useBreakpoint() === 'phone';
  const popRef = useRef(null);
  const pos = usePosition(anchorRef, popRef, open && !phone, align);
  const close = useCallback((refocus) => { onClose?.(); if (refocus) anchorRef.current?.focus?.(); }, [onClose, anchorRef]);

  useEffect(() => {
    if (!open || phone) return undefined;
    const onDown = (e) => {
      if (anchorRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      close(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(true); } };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, phone, close, anchorRef]);

  if (!open) return null;
  if (phone) {
    return (
      <Sheet open onClose={() => close(true)} title={title}>
        <div id={id} role={role} aria-label={role ? (label || title) : undefined} className={cx('ui-pop__list', className)} onKeyDown={onKeyDown}>{children}</div>
      </Sheet>
    );
  }
  return createPortal(
    <div ref={popRef} id={id} role={role} aria-label={role ? (label || title) : undefined} className={cx('ui-pop', className)} onKeyDown={onKeyDown}
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}>
      {title && <div className="ui-menu__title" aria-hidden="true">{title}</div>}
      {children}
    </div>,
    document.body,
  );
}

// Arrow keys, Home and End over the items of a list (menu items or listbox options).
function moveFocus(e, root, selector) {
  const list = [...(root?.querySelectorAll(selector) || [])];
  if (!list.length) return false;
  const at = list.indexOf(document.activeElement);
  const go = (i) => { e.preventDefault(); list[(i + list.length) % list.length]?.focus(); return true; };
  if (e.key === 'ArrowDown') return go(at + 1);
  if (e.key === 'ArrowUp') return go(at - 1);
  if (e.key === 'Home') return go(0);
  if (e.key === 'End') return go(list.length - 1);
  return false;
}

// A menu of actions. items: [{ label, onSelect, danger?, checked?, icon? }]. The trigger is a
// button (the ⋯ by default). Opening puts focus on the first item; Tab closes the menu.
export function Menu({ items, label = 'More actions', title, buttonClassName, align = 'right', children }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const phone = useBreakpoint() === 'phone';

  useEffect(() => {
    if (!open) return;
    // After the portal mounts: focus the first item.
    const t = setTimeout(() => document.getElementById(id)?.querySelector('[role^="menuitem"]')?.focus(), 0);
    return () => clearTimeout(t); // eslint-disable-line consistent-return
  }, [open, id]);

  const close = (refocus = true) => { setOpen(false); if (refocus) buttonRef.current?.focus(); };
  const onKey = (e) => {
    if (moveFocus(e, document.getElementById(id), '[role^="menuitem"]')) return;
    if (e.key === 'Tab' && !phone) close(false);
  };
  const select = (item) => { close(); item.onSelect?.(); };

  return (
    <span className="ui-menu-anchor">
      <button ref={buttonRef} type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
        aria-label={children ? undefined : label} className={buttonClassName || 'ui-btn ui-btn--overflow'}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}>
        {children || <span aria-hidden="true">⋯</span>}
      </button>
      <Popover anchorRef={buttonRef} open={open} onClose={() => setOpen(false)} title={title} align={align}
        id={id} role="menu" label={title || label} className="ui-menu" onKeyDown={onKey}>
        <div ref={listRef} className="ui-menu__items">
          {items.map((item) => (
            <button key={item.label} type="button" tabIndex={-1}
              role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
              aria-checked={item.checked === undefined ? undefined : !!item.checked}
              className={cx('ui-menu__item', item.danger && 'ui-menu__item--danger')} onClick={() => select(item)}>
              {item.icon && <span aria-hidden="true">{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </div>
      </Popover>
    </span>
  );
}

// The ⋯ menu (UI-2's name for it), now on the shared popover and sheet.
export function OverflowMenu(props) { return <Menu {...props} />; }

// The icon tile: a 26px pastel rounded square with an emoji.
export function IconTile({ icon, color = 'pastel-neutral' }) {
  return <span className="ui-icon-tile" style={{ '--ui-bg': v(color) }} aria-hidden="true">{icon}</span>;
}

// RichDropdown: a long list of choices, each an icon tile, a label and a ✓ when chosen (task
// types). A listbox: arrows move, Enter or Space chooses, Escape closes. options: [{ value,
// label, icon, color }].
export function RichDropdown({ label, options, value, onChange, placeholder = 'Choose…', id: idProp }) {
  const [open, setOpen] = useState(false);
  const auto = useId();
  const id = idProp || auto;
  const listId = `${id}-list`;
  const buttonRef = useRef(null);
  const chosen = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const list = document.getElementById(listId);
      (list?.querySelector('[aria-selected="true"]') || list?.querySelector('[role="option"]'))?.focus();
    }, 0);
    return () => clearTimeout(t); // eslint-disable-line consistent-return
  }, [open, listId]);

  const choose = (o) => { onChange(o.value); setOpen(false); buttonRef.current?.focus(); };
  const onKey = (e) => {
    if (moveFocus(e, document.getElementById(listId), '[role="option"]')) return;
    if (e.key === 'Tab') setOpen(false);
  };

  return (
    <span className="ui-menu-anchor ui-dropdown">
      <button ref={buttonRef} id={id} type="button" className="ui-input ui-dropdown__trigger" aria-haspopup="listbox" aria-expanded={open}
        aria-controls={open ? listId : undefined} aria-label={label ? `${label}: ${chosen ? chosen.label : placeholder}` : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) { e.preventDefault(); setOpen(true); } }}>
        {chosen ? <><IconTile icon={chosen.icon} color={chosen.color} /><span className="ui-dropdown__value">{chosen.label}</span></> : <span className="ui-dropdown__placeholder">{placeholder}</span>}
        <span className="ui-dropdown__chevron" aria-hidden="true">▾</span>
      </button>
      <Popover anchorRef={buttonRef} open={open} onClose={() => setOpen(false)} title={label} align="left"
        id={listId} role="listbox" label={label} className="ui-menu ui-dropdown__list" onKeyDown={onKey}>
        {options.map((o) => (
          <div key={o.value} role="option" tabIndex={-1} aria-selected={o.value === value} className="ui-menu__item ui-dropdown__option"
            onClick={() => choose(o)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(o); } }}>
            <IconTile icon={o.icon} color={o.color} />
            <span>{o.label}</span>
            {o.value === value && <span className="ui-dropdown__tick" aria-hidden="true">✓</span>}
          </div>
        ))}
      </Popover>
    </span>
  );
}

// MoveToSheet: the "Move to..." picker, the tap alternative to every drag (addendum 7: pipeline
// stages, board columns, calendar days). A bottom sheet on a phone, a small dialog elsewhere.
// destinations: [{ id, label, icon?, color?, hint? }]; current: where the item is now (shown,
// ticked and not choosable). create (optional, 2026-10-23): a "+ New folder" row at the end that
// becomes a name field; create.onSave(name) answers { dest } (then it moves there) or { error } (said
// in the sheet, the name kept to fix).
export function MoveToSheet({ open, onClose, title = 'Move to…', itemName, destinations, current, onMove, create }) {
  return (
    <Sheet open={open} onClose={onClose} title={itemName ? `Move "${itemName}" to…` : title}>
      <div role="list" className="ui-move__list">
        {destinations.map((d) => {
          const here = d.id === current;
          return (
            <div role="listitem" key={d.id}>
              <button type="button" className="ui-menu__item ui-move__item" disabled={here} aria-current={here ? 'true' : undefined}
                onClick={() => { onMove(d); onClose?.(); }}>
                {d.icon !== undefined && <IconTile icon={d.icon} color={d.color} />}
                <span className="ui-move__label">{d.label}{d.hint && <span className="ui-move__hint">{d.hint}</span>}</span>
                {here && <span className="ui-move__here">Here now ✓</span>}
              </button>
            </div>
          );
        })}
        {create && <MoveToCreate key={open ? 'open' : 'closed'} create={create} onMove={onMove} onClose={onClose} />}
      </div>
    </Sheet>
  );
}

function MoveToCreate({ create, onMove, onClose }) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = name.trim();
    if (!n || saving) return;
    setSaving(true); setError('');
    const r = (await create.onSave(n)) || {};
    setSaving(false);
    if (r.dest) { onMove(r.dest); onClose?.(); } else setError(r.error || 'That folder was not made. Nothing was moved.');
  };
  if (!naming) {
    return (
      <div role="listitem">
        <button type="button" className="ui-menu__item ui-move__item" onClick={() => setNaming(true)}>{create.label || '+ New folder'}</button>
      </div>
    );
  }
  return (
    <div role="listitem" className="ui-move__new">
      <Input autoFocus value={name} aria-label={create.inputLabel || 'New folder name'} placeholder="Folder name"
        onChange={(e) => { setName(e.target.value); setError(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setNaming(false); }} />
      {error && <p className="ui-move__error" role="alert">{error}</p>}
      <div className="ui-move__new-actions">
        <Button variant="confirm" size="sm" onClick={save} disabled={!name.trim() || saving}>{saving ? 'Saving…' : (create.saveLabel || 'Add and move')}</Button>
        <Button variant="tertiary" size="sm" onClick={() => setNaming(false)}>Cancel</Button>
      </div>
    </div>
  );
}
